import type { SiteListItem } from "./useSiteList";

export interface SelectableSite {
  id: string;
  credentialIds: string[];
}

export type SiteSelectionState =
  | { mode: "explicit"; sites: Map<string, string[]> }
  | { mode: "allFiltered" };

export const EMPTY_SELECTION: SiteSelectionState = { mode: "explicit", sites: new Map() };

export type SelectAllBannerState =
  | { kind: "hidden" }
  | { kind: "pageSelected"; pageCount: number; totalCount: number }
  | { kind: "allFiltered"; totalCount: number };

/**
 * Правила отметки сайтов в списке: что считается отмеченным, что делает клик по
 * строке и по заголовку страницы, и что показывает баннер «выбрать всё
 * найденное». Состояние отметки — значение (SiteSelectionState), каждое
 * действие возвращает новое.
 */
export class SiteSelection {
  public static toSelectableSite(item: SiteListItem): SelectableSite {
    return {
      id: item.id,
      credentialIds: item.credentials.map((credential) => credential.id),
    };
  }

  public static isSiteSelected(selection: SiteSelectionState, siteId: string): boolean {
    const selected = selection.mode === "allFiltered" || selection.sites.has(siteId);
    return selected;
  }

  public static areAllPageRowsSelected(
    selection: SiteSelectionState,
    pageSiteIds: string[],
  ): boolean {
    let areAllPageRowsSelectedOutcome: boolean;
    if (selection.mode === "allFiltered") {
      areAllPageRowsSelectedOutcome = true;
    } else {
      const sites = selection.sites;
      const allSelected = pageSiteIds.length > 0 && pageSiteIds.every((siteId) => sites.has(siteId));
      areAllPageRowsSelectedOutcome = allSelected;
    }
    return areAllPageRowsSelectedOutcome;
  }

  public static isPartialPageSelection(
    selection: SiteSelectionState,
    pageSiteIds: string[],
  ): boolean {
    let partialPageSelection: boolean;
    if (selection.mode === "allFiltered") {
      partialPageSelection = false;
    } else {
      const sites = selection.sites;
      const someSelected = pageSiteIds.some((siteId) => sites.has(siteId));
      const partial = someSelected && !SiteSelection.areAllPageRowsSelected(selection, pageSiteIds);
      partialPageSelection = partial;
    }
    return partialPageSelection;
  }

  public static toggleSite(
    selection: SiteSelectionState,
    site: SelectableSite,
  ): SiteSelectionState {
    let toggleSiteOutcome: SiteSelectionState;
    if (selection.mode === "allFiltered") {
      toggleSiteOutcome = EMPTY_SELECTION;
    } else {
      const sites = new Map(selection.sites);
      if (sites.has(site.id)) {
        sites.delete(site.id);
      } else {
        sites.set(site.id, site.credentialIds);
      }
      toggleSiteOutcome = { mode: "explicit", sites };
    }
    return toggleSiteOutcome;
  }

  public static toggleCurrentPage(
    selection: SiteSelectionState,
    pageSites: SelectableSite[],
  ): SiteSelectionState {
    let toggleCurrentPageOutcome: SiteSelectionState;
    if (selection.mode === "allFiltered") {
      toggleCurrentPageOutcome = EMPTY_SELECTION;
    } else {
      const pageSiteIds = pageSites.map((site) => site.id);
      const sites = new Map(selection.sites);
      if (SiteSelection.areAllPageRowsSelected(selection, pageSiteIds)) {
        for (const site of pageSites) {
          sites.delete(site.id);
        }
      } else {
        for (const site of pageSites) {
          sites.set(site.id, site.credentialIds);
        }
      }
      toggleCurrentPageOutcome = { mode: "explicit", sites };
    }
    return toggleCurrentPageOutcome;
  }

  public static selectAllFiltered(): SiteSelectionState {
    return { mode: "allFiltered" };
  }

  public static selectedSiteCount(selection: SiteSelectionState): number {
    const count = selection.mode === "allFiltered" ? 0 : selection.sites.size;
    return count;
  }

  public static selectedSiteIds(selection: SiteSelectionState): string[] {
    const siteIds = selection.mode === "allFiltered" ? [] : Array.from(selection.sites.keys());
    return siteIds;
  }

  public static selectedCredentialIds(selection: SiteSelectionState): string[] {
    let selectedCredentialIdsOutcome: string[];
    if (selection.mode === "allFiltered") {
      selectedCredentialIdsOutcome = [];
    } else {
      const credentialIds: string[] = [];
      for (const siteCredentialIds of selection.sites.values()) {
        for (const credentialId of siteCredentialIds) {
          credentialIds.push(credentialId);
        }
      }
      selectedCredentialIdsOutcome = credentialIds;
    }
    return selectedCredentialIdsOutcome;
  }

  public static computeSelectAllBannerState(
    selection: SiteSelectionState,
    pageSiteIds: string[],
    totalCount: number,
  ): SelectAllBannerState {
    let selectAllBannerState: SelectAllBannerState;
    if (selection.mode === "allFiltered") {
      selectAllBannerState = { kind: "allFiltered", totalCount };
    } else {
      const allPageSelected = SiteSelection.areAllPageRowsSelected(selection, pageSiteIds);
      const banner: SelectAllBannerState =
        allPageSelected && totalCount > pageSiteIds.length
          ? { kind: "pageSelected", pageCount: pageSiteIds.length, totalCount }
          : { kind: "hidden" };
      selectAllBannerState = banner;
    }
    return selectAllBannerState;
  }
}
